import { CheckboxInput, Modal, PropertyList } from '@app/components'
import { showMessageNotification, showMessagesNotifications, useConfirmPaymentSetup } from '@app/lib'
import { type SugarPaymentSetupConfirmable } from '@app/types'
import { Button, Group } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { isEmpty } from 'lodash'
import { useCallback, useState } from 'react'

import { type PaymentSetupConfig, toPaymentSetupItems } from './paymentSetupConfig'

export interface PaymentSetupConfirmModalProps<T extends SugarPaymentSetupConfirmable> {
  config: PaymentSetupConfig<T>
  onClose: () => void
  opened: boolean
  record: T | null
}

// Built on Modal, not ConfirmModal: ConfirmModal's onConfirm is sync-typed and closeOnConfirm calls
// onClose() on the next line without awaiting, so isLoading and closeOnConfirm are mutually unusable.
// This modal needs the spinner to last until the confirm mutation actually resolves.
export const PaymentSetupConfirmModal = <T extends SugarPaymentSetupConfirmable>({
  config,
  onClose,
  opened,
  record
}: PaymentSetupConfirmModalProps<T>) => {
  const { isPending: isConfirming, mutateAsync: confirmPaymentSetup } = useConfirmPaymentSetup(config.paymentSetupType)
  const [isAttested, setIsAttested] = useState(false)
  // Modal's keepMounted={false} only stops Modal from rendering its own children while closed — it
  // cannot unmount this component, which owns isAttested and is what renders <Modal>. Every call site
  // toggles opened via useDisclosure rather than remounting this component, so the checkbox state
  // outlives a close/reopen cycle unless we clear it ourselves.
  const handleClose = useCallback(() => {
    setIsAttested(false)
    onClose()
  }, [onClose])
  const handleConfirm = useCallback(async () => {
    try {
      const { messages, success } = await confirmPaymentSetup()

      if (success) {
        handleClose()
      }

      // showMessagesNotifications([]) is a silent no-op, so an empty messages array would leave the
      // user with no feedback at all. Always fall back to something.
      if (isEmpty(messages)) {
        showMessageNotification({
          message: success
            ? 'Thank you — we have recorded your confirmation.'
            : 'We could not save your confirmation. Please try again.',
          type: success ? 'success' : 'error'
        })
      } else {
        showMessagesNotifications(messages)
      }
    } catch {
      // apiClient resolves undefined on any non-2xx, so this is a destructuring TypeError rather than
      // anything a human can act on. No existing call site wraps mutateAsync, but the alternative here
      // is an unhandled rejection and total silence.
      showMessageNotification({
        message: 'We could not save your confirmation. Please try again.',
        type: 'error'
      })
    }
  }, [confirmPaymentSetup, handleClose])

  return (
    <Modal
      isLoading={isConfirming}
      onClose={handleClose}
      opened={opened}
      size='lg'
      title={config.title}
    >
      {record && (
        <>
          <PropertyList items={toPaymentSetupItems(config, record)} />
          <CheckboxInput
            checked={isAttested}
            disabled={isConfirming}
            label={config.attestationLabel}
            mt='lg'
            onChange={(event) => setIsAttested(event.currentTarget.checked)}
          />
          <Group
            mt='xl'
            position='right'
          >
            <Button
              disabled={isConfirming}
              onClick={handleClose}
              variant='default'
            >
              Cancel
            </Button>
            <Button
              disabled={!isAttested || isConfirming}
              leftIcon={<IconCheck size={18} />}
              loading={isConfirming}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Group>
        </>
      )}
    </Modal>
  )
}
